
import React, { useState, useEffect, useRef } from 'react';
import { Responsive as ResponsiveGridLayout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { APIClient } from '../types';
import { backendService } from '../services/backendService';

const DEFAULT_LAYOUT = [
  { i: 'registry', x: 0, y: 0, w: 8, h: 12 },
  { i: 'inspector', x: 8, y: 0, w: 4, h: 12 }
];

interface ClientManagerProps {
  clients: APIClient[];
  setClients: React.Dispatch<React.SetStateAction<APIClient[]>>;
}

const ClientManager: React.FC<ClientManagerProps> = ({ clients, setClients }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Partial<APIClient> | null>(null);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [usageByClientId, setUsageByClientId] = useState<Record<string, any>>({});
  const [tokenByClientId, setTokenByClientId] = useState<Record<string, string>>({});

  // Draggable Layout State
  const [isLocked, setIsLocked] = useState(true);
    const [layouts, setLayouts] = useState<any>({ lg: DEFAULT_LAYOUT });
  const [mounted, setMounted] = useState(false);
  
  // Custom width provider logic
  const [width, setWidth] = useState(1200);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
    const saved = localStorage.getItem('omnigate_client_layout');
    if (saved) {
      try { const p = JSON.parse(saved);
                if (p) { Object.keys(p).forEach(k => { if (Array.isArray(p[k])) p[k].forEach((i:any) => delete i.static); }); }
                setLayouts(p); } catch { }
    }

    if (containerRef.current) {
      setWidth(containerRef.current.clientWidth);
    }

    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        if (entry.contentRect.width > 0) {
          setWidth(entry.contentRect.width);
        }
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  const onLayoutChange = (layout: any, allLayouts: any) => {
    setLayouts(allLayouts);
    localStorage.setItem('omnigate_client_layout', JSON.stringify(allLayouts));
  };

  const setActionFeedback = (type: 'success' | 'error' | 'info', text: string) => {
    setFeedback({ type, text });
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === clients.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(clients.map(c => c.id)));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingClient?.name) return;

    setSaving(true);
    try {
      if (editingClient.id) {
        const success = await backendService.updateClient(editingClient.id, editingClient);
        if (success) {
          setClients(prev => prev.map(c => c.id === editingClient.id ? { ...c, ...editingClient } as APIClient : c));
        }
      } else {
        const newClient = await backendService.addClient(editingClient);
        if (newClient) {
          setClients(prev => [...prev, newClient]);
          alert(`New Credential Provisioned!\n\nAPI KEY: ${newClient.apiKey}\n\nWARNING: This key is only shown once.`);
        }
      }
      setShowModal(false);
      setEditingClient(null);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (client: APIClient) => {
    const nextStatus = !client.isActive;
    const success = await backendService.updateClient(client.id, { isActive: nextStatus });
    if (success) {
      setClients(prev => prev.map(c => c.id === client.id ? { ...c, isActive: nextStatus } : c));
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Permanently revoke this credential? All associated node access will be terminated immediately.')) return;
    const success = await backendService.deleteClient(id);
    if (success) {
      setClients(prev => prev.filter(c => c.id !== id));
      setActionFeedback('success', `Credential ${id} revoked.`);
    } else {
      setActionFeedback('error', `Failed to revoke credential ${id}.`);
    }
  };

  const handleValidate = async (client: APIClient) => {
    const candidateKey = client.apiKey || prompt(`Provide API key value to validate for ${client.name}:`) || '';
    if (!candidateKey) {
      setActionFeedback('info', 'Validation canceled (no key provided).');
      return;
    }

    setActionBusy(`validate:${client.id}`);
    try {
      const result = await backendService.validateApiKey(candidateKey);
      if (result.valid) {
        setActionFeedback('success', `Key for ${client.name} is valid.`);
      } else {
        setActionFeedback('error', `Key for ${client.name} invalid. ${result.reason || result.error || ''}`.trim());
      }
    } finally {
      setActionBusy(null);
    }
  };

  const handleUsage = async (client: APIClient) => {
    setActionBusy(`usage:${client.id}`);
    try {
      const result = await backendService.getClientUsage(client.id);
      if (!result.success) {
        setActionFeedback('error', result.error || `Failed to load usage for ${client.name}.`);
        return;
      }

      setUsageByClientId(prev => ({ ...prev, [client.id]: result.usage }));
      setActionFeedback('success', `Usage loaded for ${client.name}.`);
    } finally {
      setActionBusy(null);
    }
  };

  const handleGenerateToken = async (client: APIClient) => {
    setActionBusy(`token:${client.id}`);
    try {
      const result = await backendService.generateClientToken(client.id);
      if (!result.success || !result.token) {
        setActionFeedback('error', result.error || `Failed to generate token for ${client.name}.`);
        return;
      }

      setTokenByClientId(prev => ({ ...prev, [client.id]: result.token! }));
      setActionFeedback('success', `JWT token generated for ${client.name}.`);
    } finally {
      setActionBusy(null);
    }
  };

  const copyToken = async (clientId: string) => {
    const token = tokenByClientId[clientId];
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      setActionFeedback('success', 'Token copied to clipboard.');
    } catch {
      setActionFeedback('error', 'Could not copy token automatically.');
    }
  };

  const getPriorityColor = (p: string) => {
    switch (p) {
      case 'HIGH': return 'text-red-400 bg-red-400/10 border-red-400/20';
      case 'MEDIUM': return 'text-purple-400 bg-purple-400/10 border-purple-400/20';
      default: return 'text-slate-400 bg-slate-400/10 border-slate-400/20';
    }
  };

  if (!mounted) return null;

  return (
    <div ref={containerRef} className="flex flex-col h-full bg-[#131722] overflow-hidden min-h-[600px] relative pb-10">
      <style>{`
          .react-grid-layout { position: relative; transition: height 200ms ease; }
          .react-grid-item { transition: all 200ms ease; transition-property: left, top; }
          .react-grid-item.cssTransforms { transition-property: transform; }
          .react-grid-item.resizing { z-index: 1; will-change: width, height; }
          .react-grid-item.react-draggable-dragging { transition: none; z-index: 3; will-change: transform; }
          .react-grid-item.react-grid-placeholder { background: rgba(168, 85, 247, 0.2); border: 2px dashed rgba(168, 85, 247, 0.5); opacity: 0.5; z-index: 2; transition: none; }
          .drag-handle { cursor: grab; }
          .drag-handle:active { cursor: grabbing; }
          .panel-wrapper { height: 100%; display: flex; flex-direction: column; background: #1e222d; border: 1px solid #2a2e39; border-radius: 0.5rem; overflow: hidden; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.5); }
          .panel-wrapper:hover { border-color: rgba(168, 85, 247, 0.3); }
      `}</style>

      <div className="absolute -top-10 right-4 z-50 flex gap-2">
        <button onClick={() => setIsLocked(!isLocked)} className={`text-[9px] px-2 py-1 rounded border transition-colors ${isLocked ? 'bg-red-900/40 text-red-400 border-red-800' : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white'}`}>
          <i className={`fas ${isLocked ? 'fa-lock' : 'fa-unlock'} mr-1`}></i> {isLocked ? 'Locked' : 'Unlocked'}
        </button>
        <button onClick={() => { setLayouts({ lg: DEFAULT_LAYOUT }); localStorage.removeItem('omnigate_client_layout'); }} className="text-[9px] bg-slate-800 text-slate-400 hover:text-white px-2 py-1 rounded border border-slate-700">
          <i className="fas fa-sync-alt mr-1"></i> Reset Layout
        </button>
      </div>

      <div className={`flex justify-between items-start px-2 ${!isLocked ? 'drag-handle' : 'cursor-default'} w-full flex-shrink-0 z-10 mb-2 mt-4`}>
        <div>
          <h2 className="text-xl font-black text-white uppercase italic tracking-tighter">Access <span className="text-purple-500">Quotas</span></h2>
          <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1">API Lifecycle Management</p>
        </div>
        <button
          onClick={() => {
            setEditingClient({ name: '', tokenLimit: 100000, priority: 'MEDIUM' });
          }}
          className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded font-black text-[10px] uppercase tracking-widest shadow-xl transition-all"
        >
          + Provision Node Access
        </button>
      </div>

      {feedback && (
        <div className={`mx-2 mb-2 px-4 py-2 text-[10px] rounded font-bold uppercase tracking-wider border ${feedback.type === 'success' ? 'text-green-400 bg-green-500/10 border-green-500/20' :
          feedback.type === 'error' ? 'text-red-400 bg-red-500/10 border-red-500/20' :
            'text-blue-300 bg-blue-500/10 border-blue-500/20'
          }`}>
          {feedback.text}
        </div>
      )}

      
                <div className="flex justify-between items-center sm:mb-4 mb-2 px-2 bg-[#131722]/80 border border-[#2a2e39] rounded py-2">
                    <div className="flex items-center gap-2">
                        <button onClick={() => setIsLocked(!isLocked)} className={`text-[10px] px-3 py-1.5 rounded transition-all font-bold ${isLocked ? 'bg-red-500/20 text-red-400 border border-red-500/50 shadow-[0_0_10px_rgba(239,68,68,0.2)]' : 'bg-green-500/20 text-green-400 border border-green-500/50'}`}>
                            <i className={`fas ${isLocked ? 'fa-lock' : 'fa-unlock'} mr-1`}></i> {isLocked ? 'Blokada Włączona' : 'Odblokowane (Przeciągaj)'}
                        </button>
                    </div>
                </div>
    <ResponsiveGridLayout
        className="layout flex-1"
        width={width}
        layouts={isLocked ? Object.fromEntries(Object.entries(layouts).map(([k, v]) => [k, v.map((i: any) => ({ ...i, static: true }))])) : layouts}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
        rowHeight={50}
        onLayoutChange={onLayoutChange}
                // @ts-ignore
                isDraggable={!isLocked}
                // @ts-ignore
                isResizable={!isLocked}
        // @ts-ignore
        draggableHandle=".drag-handle"
        margin={[12, 12]}
      >
        {/* REGISTRY PANEL */}
        <div key="registry" className="panel-wrapper">
          <div className={`p-2 border-b border-[#2a2e39] bg-[#131722]/60 flex items-center justify-between ${!isLocked ? 'drag-handle' : 'cursor-default'} flex-shrink-0`}>
            <div className="flex items-center gap-2">
              <i className="fas fa-users-cog text-purple-500"></i>
              <h3 className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Active Access Registry</h3>
            </div>
            <i className="fas fa-grip-lines text-slate-600"></i>
          </div>
          <div className="flex-1 overflow-y-auto bg-[#0a0c10] custom-scrollbar">
            {feedback && (
              <div className={`px-4 py-2 text-[10px] font-bold uppercase tracking-wider border-b border-[#2a2e39] ${feedback.type === 'success' ? 'text-green-400 bg-green-500/10' :
                feedback.type === 'error' ? 'text-red-400 bg-red-500/10' :
                  'text-blue-300 bg-blue-500/10'
                }`}>
                {feedback.text}
              </div>
            )}
            <table className="w-full text-left border-collapse font-mono text-[10px]">
              <thead>
                <tr className="bg-[#131722]/50 border-b border-[#2a2e39]">
                  <th className="p-4 w-10 text-center">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === clients.length && clients.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded bg-[#131722] border-[#2a2e39] accent-purple-500 cursor-pointer"
                    />
                  </th>
                  <th className="p-4 font-bold uppercase text-slate-500">Identity / ID</th>
                  <th className="p-4 font-bold uppercase text-slate-500">Token Quota</th>
                  <th className="p-4 font-bold uppercase text-slate-500">Priority</th>
                  <th className="p-4 font-bold uppercase text-slate-500">Status</th>
                  <th className="p-4 font-bold uppercase text-slate-500 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#2a2e39]/40">
                {clients.map(client => (
                  <tr key={client.id} className={`hover:bg-[#2a2e39]/20 transition-all ${selectedIds.has(client.id) ? 'bg-purple-500/5' : ''}`}>
                    <td className="p-4 text-center">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(client.id)}
                        onChange={() => toggleSelect(client.id)}
                        className="rounded bg-[#131722] border-[#2a2e39] accent-purple-500 cursor-pointer"
                      />
                    </td>
                    <td className="p-4">
                      <div className="font-bold text-slate-200 uppercase">{client.name}</div>
                      <div className="text-[8px] text-slate-500 mt-0.5">{client.id}</div>
                    </td>
                    <td className="p-4">
                      <div className="text-yellow-500 font-black">{client.tokenLimit.toLocaleString()}</div>
                      <div className="text-[8px] text-slate-600 uppercase">Total Capacity</div>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-0.5 rounded border text-[8px] font-black ${getPriorityColor(client.priority)}`}>
                        {client.priority}
                      </span>
                    </td>
                    <td className="p-4">
                      <button
                        onClick={() => handleToggleActive(client)}
                        className={`px-2 py-0.5 rounded border text-[8px] font-black transition-all ${client.isActive
                          ? 'text-green-500 border-green-500/20 bg-green-500/10 hover:bg-green-500/20'
                          : 'text-red-500 border-red-500/20 bg-red-500/10 hover:bg-red-500/20'
                          }`}
                      >
                        {client.isActive ? 'AUTHORIZED' : 'LOCKED'}
                      </button>
                    </td>
                    <td className="p-4 text-right space-x-2">
                      <button
                        onClick={() => handleValidate(client)}
                        className="text-slate-500 hover:text-blue-300 transition-colors"
                        title="Validate Key"
                        disabled={actionBusy === `validate:${client.id}`}
                      >
                        <i className="fas fa-check-circle"></i>
                      </button>
                      <button
                        onClick={() => handleUsage(client)}
                        className="text-slate-500 hover:text-cyan-300 transition-colors"
                        title="Usage Details"
                        disabled={actionBusy === `usage:${client.id}`}
                      >
                        <i className="fas fa-chart-line"></i>
                      </button>
                      <button
                        onClick={() => handleGenerateToken(client)}
                        className="text-slate-500 hover:text-yellow-300 transition-colors"
                        title="Generate JWT"
                        disabled={actionBusy === `token:${client.id}`}
                      >
                        <i className="fas fa-key"></i>
                      </button>
                      <button
                        onClick={() => { setEditingClient(client); setShowModal(true); }}
                        className="text-slate-500 hover:text-white transition-colors"
                        title="Edit Quotas"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        onClick={() => handleDelete(client.id)}
                        className="text-slate-600 hover:text-red-500 transition-colors"
                        title="Revoke Key"
                      >
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {clients.length === 0 && (
              <div className="py-20 text-center text-slate-600 font-black uppercase tracking-widest opacity-20">
                No Active Credentials
              </div>
            )}
          </div>
        </div>

        {/* INSPECTOR & CONFIG PANEL */}
        <div key="inspector" className="panel-wrapper">
          <div className={`p-2 border-b border-[#2a2e39] bg-[#131722]/60 flex items-center justify-between ${!isLocked ? 'drag-handle' : 'cursor-default'} flex-shrink-0`}>
            <div className="flex items-center gap-2">
              <i className="fas fa-sliders-h text-orange-500"></i>
              <h3 className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Node Provisioning & Config</h3>
            </div>
            <i className="fas fa-grip-lines text-slate-600"></i>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-6 bg-[#0a0c10] custom-scrollbar">

            {editingClient ? (
              <form onSubmit={handleSave} className="bg-[#1e222d] border border-purple-500/30 rounded shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in duration-200">
                <div className="p-3 border-b border-[#2a2e39] bg-[#131722]/60 flex justify-between items-center">
                  <h3 className="text-[10px] font-black text-white uppercase tracking-widest">{editingClient?.id ? 'Adjust' : 'Provision'} Credential</h3>
                  <button type="button" onClick={() => setEditingClient(null)} className="text-slate-500 hover:text-white"><i className="fas fa-times"></i></button>
                </div>

                <div className="p-4 space-y-3">
                  <div className="space-y-1">
                    <label className="text-[9px] font-bold text-slate-500 uppercase">Client Name / Project</label>
                    <input
                      required
                      autoFocus
                      value={editingClient?.name || ''}
                      onChange={e => setEditingClient(p => ({ ...p!, name: e.target.value }))}
                      placeholder="e.g. Finance Agent Alpha"
                      className="w-full bg-[#131722] border border-[#2a2e39] rounded px-3 py-1.5 text-[10px] text-white focus:border-purple-500 outline-none font-mono"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[9px] font-bold text-slate-500 uppercase">Token Limit</label>
                      <input
                        type="number"
                        value={editingClient?.tokenLimit || 100000}
                        onChange={e => setEditingClient(p => ({ ...p!, tokenLimit: parseInt(e.target.value) }))}
                        className="w-full bg-[#131722] border border-[#2a2e39] rounded px-3 py-1.5 text-[10px] text-white focus:border-purple-500 outline-none font-mono"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[9px] font-bold text-slate-500 uppercase">Queue Priority</label>
                      <select
                        value={editingClient?.priority || 'MEDIUM'}
                        onChange={e => setEditingClient(p => ({ ...p!, priority: e.target.value as any }))}
                        className="w-full bg-[#131722] border border-[#2a2e39] rounded px-3 py-1.5 text-[10px] text-white focus:border-purple-500 outline-none"
                      >
                        <option value="LOW">LOW</option>
                        <option value="MEDIUM">MEDIUM</option>
                        <option value="HIGH">HIGH</option>
                      </select>
                    </div>
                  </div>

                  {!editingClient?.id && (
                    <div className="p-2 bg-purple-500/5 border border-purple-500/10 rounded">
                      <p className="text-[8px] text-purple-400 leading-relaxed italic">
                        <i className="fas fa-info-circle mr-1"></i>
                        Keys are cryptographically generated and masked for security.
                      </p>
                    </div>
                  )}
                </div>

                <div className="p-3 bg-[#131722]/40 border-t border-[#2a2e39] flex gap-2">
                  <button
                    type="button"
                    onClick={() => setEditingClient(null)}
                    className="flex-1 py-1.5 bg-[#2a2e39] text-slate-400 rounded text-[9px] font-black uppercase hover:text-white transition-all"
                  >
                    Discard
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 py-1.5 bg-purple-600 text-white rounded text-[9px] font-black uppercase shadow-lg shadow-purple-900/20 hover:bg-purple-500 transition-all disabled:opacity-50"
                  >
                    {saving ? 'PROVISIONING...' : (editingClient?.id ? 'UPDATE QUOTA' : 'GENERATE KEY')}
                  </button>
                </div>
              </form>
            ) : (
              <div className="p-6 text-center text-slate-600 font-bold uppercase tracking-widest text-[10px] border border-dashed border-[#2a2e39] rounded bg-[#131722]/50">
                Select a node to view configuration or Provision a new one.
              </div>
            )}

            <div className="space-y-3">
              {clients.map((client) => {
                const usage = usageByClientId[client.id];
                const token = tokenByClientId[client.id];
                if (!usage && !token) return null;

                return (
                  <div key={`details-${client.id}`} className="border border-[#2a2e39] p-3 bg-[#1e222d] rounded shadow-lg animate-in slide-in-from-bottom-2">
                    <div className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-2">Lifecycle Details — {client.name}</div>
                    {usage && (
                      <div className="text-[10px] text-slate-300 mb-2 p-2 bg-[#131722] rounded flex justify-between">
                        <span>Requests: <b className="text-cyan-300">{usage.total_requests ?? 0}</b></span>
                        <span>Tokens: <b className="text-yellow-300">{usage.total_tokens ?? 0}</b></span>
                      </div>
                    )}
                    {token && (
                      <div className="space-y-2">
                        <textarea
                          value={token}
                          readOnly
                          className="w-full h-20 bg-[#0f131d] border border-[#2a2e39] rounded p-2 text-[9px] text-slate-300 font-mono resize-none focus:border-purple-500"
                        />
                        <button
                          onClick={() => copyToken(client.id)}
                          className="w-full py-1.5 bg-[#2a2e39] hover:bg-[#363b48] text-[9px] text-white rounded uppercase tracking-wider font-bold transition-all"
                        >
                          Copy JWT
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </ResponsiveGridLayout>
    </div>
  );
};

export default ClientManager;
